import type { ILoanRepayment } from "@/modules/transaction/common/types";
import { DATE_FORMAT } from "@/shared/common/constants";
import { Amount } from "@/shared/components";
import { formatDate } from "@/shared/helpers";
import { Copy } from "@ab.uitools/ui-library/components/Copy";
import { Divider } from "@ab.uitools/ui-library/components/Divider";
import { Text } from "@ab.uitools/ui-library/components/Text";
import type { RefObject } from "react";
import { useTranslation } from "react-i18next";

interface Props {
  transfer: ILoanRepayment;
  scrollbarContainerRef?: RefObject<HTMLDivElement | null>;
}

export const LoanRepaymentDetailRows = ({
  transfer,
  scrollbarContainerRef
}: Props) => {
  const { t } = useTranslation();

  return (
    <div>
      <div>
        <div>
          <div className="flexbox justify-content--between pt-20 pb-20 detail-row">
            <Text
              className="text-nowrap"
              type="secondary"
              size="medium"
              weight="semibold"
            >
              {t("general.amount")}
            </Text>
            <div className="flexbox align-items--center">
              <Amount
                size="medium"
                weight="bold"
                amount={transfer.amount as number}
                currency={transfer.debitAccount?.currency}
              />
            </div>
          </div>
          <Divider
            color="light"
            type="primary"
            isHorizontal
          />
          <div className="flexbox justify-content--between pt-20 pb-20 detail-row">
            <Text
              className="text-nowrap"
              type="secondary"
              size="medium"
              weight="semibold"
            >
              {t("transfer.loanRepayment.repaymentType")}
            </Text>
            <div className="flexbox align-items--center">
              <Text size="medium" weight="bold">
                {t(`transfer.loanRepayment.${transfer.loanRepaymentType}`)}
              </Text>
            </div>
          </div>
          <Divider
            color="light"
            type="primary"
            isHorizontal
          />
          <div className="flexbox justify-content--between pt-20 pb-20 detail-row">
            <Text
              className="text-nowrap"
              type="secondary"
              size="medium"
              weight="semibold"
            >
              {t("transfer.accountFrom")}
            </Text>
            <div className="flexbox align-items--center">
              <Copy
                textAfterCopy={t("general.copied")}
                size="medium"
                parentRef={scrollbarContainerRef as RefObject<HTMLDivElement>}
                text={transfer.debitAccount?.number}
              />
              <Text size="medium" weight="bold">
                {transfer.debitAccount?.number}
              </Text>
            </div>
          </div>
          <Divider
            color="light"
            type="primary"
            isHorizontal
          />
          <div className="flexbox justify-content--between pt-20 pb-20 detail-row">
            <Text
              className="text-nowrap"
              type="secondary"
              size="medium"
              weight="semibold"
            >
              {t("general.contractNumber")}
            </Text>
            <div className="flexbox align-items--center">
              <Copy
                textAfterCopy={t("general.copied")}
                size="medium"
                parentRef={scrollbarContainerRef as RefObject<HTMLDivElement>}
                text={transfer.loanContractNumber}
              />
              <Text size="medium" weight="bold">
                {transfer.loanContractNumber}
              </Text>
            </div>
          </div>
          <Divider
            color="light"
            type="primary"
            isHorizontal
          />
          <div className="flexbox justify-content--between pt-20 pb-20 detail-row">
            <Text
              className="text-nowrap"
              type="secondary"
              size="medium"
              weight="semibold"
            >
              {t("transfer.transactionNumber")}
            </Text>
            <div className="flexbox align-items--center">
              <Copy
                textAfterCopy={t("general.copied")}
                size="medium"
                parentRef={scrollbarContainerRef as RefObject<HTMLDivElement>}
                text={transfer.transactionNumber}
              />
              <Text size="medium" weight="bold">
                {transfer.transactionNumber}
              </Text>
            </div>
          </div>
          <Divider
            color="light"
            type="primary"
            isHorizontal
          />
          <div>
            <div className="flexbox justify-content--between pt-20 pb-4 detail-row">
              <Text type="secondary" size="medium" weight="semibold">
                {t("general.date")}
              </Text>
              <Text size="medium" weight="bold">
                {formatDate(transfer.creationDate, DATE_FORMAT.YYYY_MM_DD)}
              </Text>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
